import { escapeHtml } from '../blocks/escape'
import type {
  Block,
  BlockCond,
  CompileContext,
  CompileSample,
} from '../blocks/types'

// "Preview as" sample customers, personalisation tokens and the client-side
// evaluation of the compiler's per-recipient markers (prototype ED_CUSTOMERS,
// ED_TOKENS, edVisible, edCondText). Preview only — nothing here is sent.

export interface SampleCustomer {
  id: 'gold' | 'silver' | 'platinum' | 'guest'
  label: string
  member: boolean
  tier: string // lowercase tier value, '' for non-members
  points: number
  // Index into the store's real segments this sample is treated as being in.
  segmentIdx: number[]
  cartSize: number
  vals: Record<string, string>
}

export const SAMPLE_CUSTOMERS: SampleCustomer[] = [
  {
    id: 'gold',
    label: 'Sarah (Gold, 2,450 pts)',
    member: true,
    tier: 'gold',
    points: 2450,
    segmentIdx: [0],
    cartSize: 2,
    vals: {
      first_name: 'Sarah',
      last_name: 'Chen',
      email: 'sarah.chen@example.com',
      points_balance: '2,450',
      tier_name: 'Gold',
      next_tier: 'Platinum',
      points_to_next_tier: '550',
      tier_progress_pct: '64',
      member_id: 'BSR 2045 5518',
    },
  },
  {
    id: 'silver',
    label: 'Jordan (Silver, 820 pts)',
    member: true,
    tier: 'silver',
    points: 820,
    segmentIdx: [1],
    cartSize: 2,
    vals: {
      first_name: 'Jordan',
      last_name: 'Patel',
      email: 'jordan.p@example.com',
      points_balance: '820',
      tier_name: 'Silver',
      next_tier: 'Gold',
      points_to_next_tier: '680',
      tier_progress_pct: '32',
      member_id: 'BSR 1180 0932',
    },
  },
  {
    // Top tier: the backend leaves next_tier / points_to_next_tier empty and
    // reports 100% progress.
    id: 'platinum',
    label: 'Taylor (Platinum, 4,200 pts)',
    member: true,
    tier: 'platinum',
    points: 4200,
    segmentIdx: [0],
    cartSize: 1,
    vals: {
      first_name: 'Taylor',
      last_name: 'Brooks',
      email: 'taylor.b@example.com',
      points_balance: '4,200',
      tier_name: 'Platinum',
      next_tier: '',
      points_to_next_tier: '',
      tier_progress_pct: '100',
      member_id: 'BSR 3307 7741',
    },
  },
  {
    id: 'guest',
    label: 'Alex (not a member)',
    member: false,
    tier: '',
    points: 0,
    segmentIdx: [],
    cartSize: 0,
    // Non-members get empty loyalty tokens at send time.
    vals: {
      first_name: 'Alex',
      last_name: 'Morgan',
      email: 'alex.m@example.com',
      points_balance: '',
      tier_name: '',
      next_tier: '',
      points_to_next_tier: '',
      tier_progress_pct: '0',
      member_id: '',
    },
  },
]

export const sampleById = (id: string): SampleCustomer =>
  SAMPLE_CUSTOMERS.find((c) => c.id === id) ?? SAMPLE_CUSTOMERS[0]

export const TOKEN_GROUPS: Array<{
  g: string
  items: Array<[string, string]>
}> = [
  {
    g: 'Customer',
    items: [
      ['first_name', 'First name'],
      ['last_name', 'Last name'],
      ['email', 'Email'],
    ],
  },
  {
    g: 'Loyalty',
    items: [
      ['points_balance', 'Points balance'],
      ['tier_name', 'Tier'],
      ['next_tier', 'Next tier'],
      ['points_to_next_tier', 'Points to next tier'],
      ['member_id', 'Member ID'],
    ],
  },
  { g: 'Offer', items: [['coupon', 'Coupon code']] },
  {
    g: 'Cart & store',
    items: [
      ['cart_total', 'Cart total'],
      ['cart_url', 'Cart link'],
      ['store_name', 'Store name'],
    ],
  },
]

export const TIER_CHIPS: Array<[string, string]> = [
  ['bronze', 'Bronze'],
  ['silver', 'Silver'],
  ['gold', 'Gold'],
  ['platinum', 'Platinum'],
  ['none', 'Not a member'],
]

const customerSegments = (
  c: SampleCustomer,
  segments: Array<{ id: string }>,
): string[] =>
  c.segmentIdx.map((i) => segments[i]?.id).filter((v): v is string => !!v)

// Same rule the backend applies to a bs:if marker.
const RULES = ['tier', 'segment', 'member', 'points']

// Mirrors the compiler: only a known mode (only/hide) with a known rule is a
// condition; anything else (missing, malformed) behaves as "Everyone".
export const isConditional = (
  cond: Partial<BlockCond> | null | undefined,
): boolean =>
  Boolean(cond) &&
  (cond?.mode === 'only' || cond?.mode === 'hide') &&
  RULES.includes(String(cond?.rule))

export function condMatches(
  cond: Partial<BlockCond> | null | undefined,
  c: SampleCustomer,
  segments: Array<{ id: string }>,
): boolean {
  if (!cond || !isConditional(cond)) return true
  const values = (Array.isArray(cond.values) ? cond.values : []).map(String)
  let m: boolean
  if (cond.rule === 'tier')
    m = values.map((v) => v.toLowerCase()).includes(c.tier || 'none')
  else if (cond.rule === 'segment') {
    const mine = customerSegments(c, segments)
    m = values.some((v) => mine.includes(v))
  } else if (cond.rule === 'member') m = c.member
  else m = c.points >= (Number(cond.num) || 0)
  return cond.mode === 'hide' ? !m : m
}

export const isVisibleFor = (
  b: Block,
  c: SampleCustomer,
  segments: Array<{ id: string }>,
) => condMatches(b.cond, c, segments)

// Tokens offered for this email. {{cart_total}} is only filled for
// abandoned-cart sends ({{cart_url}} works everywhere).
export function tokenGroupsFor(cartItems: boolean): typeof TOKEN_GROUPS {
  if (cartItems) return TOKEN_GROUPS
  return TOKEN_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(([k]) => k !== 'cart_total'),
  }))
}

const TIER_LABEL: Record<string, string> = Object.fromEntries(TIER_CHIPS)

// Condition pill text: "Only Gold tier", "Hidden for 1,500+ points".
// null when the block has no (valid) condition.
export function condText(
  b: Block,
  segments: Array<{ id: string; name: string }>,
): string | null {
  const c = b?.cond
  if (!c || !isConditional(c)) return null
  const vals = (Array.isArray(c.values) ? c.values : []).map(String)
  let what: string
  if (c.rule === 'tier')
    what = vals.length
      ? vals
          .map((v) =>
            v === 'none' ? 'non-members' : (TIER_LABEL[v.toLowerCase()] ?? v),
          )
          .join(', ') + (vals.length === 1 && vals[0] !== 'none' ? ' tier' : '')
      : 'no tiers picked yet'
  else if (c.rule === 'segment')
    what = vals.length
      ? vals
          .map((k) => segments.find((s) => s.id === k)?.name ?? 'a segment')
          .join(', ')
      : 'no segments picked yet'
  else if (c.rule === 'member') what = 'loyalty members'
  else what = `${(Number(c.num) || 0).toLocaleString('en-US')}+ points`
  return `${c.mode === 'hide' ? 'Hidden for ' : 'Only '}${what}`
}

// ── Preview rendering ────────────────────────────────────────────────────────

const PLACEHOLDER_IMG =
  'data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2264%22 height=%2264%22%3E%3Crect width=%2264%22 height=%2264%22 fill=%22%23E5E7EB%22/%3E%3C/svg%3E'

const SAMPLE_CART = [
  { name: 'Grandaddy Purple', variant: '7g · Indica', price: 49.99 },
  { name: 'Sour Diesel Cart', variant: '1g · Sativa', price: 44.99 },
]

const money = (n: number) => `$${n.toFixed(2)}`

const IF_RE = /<!--bs:if (\{[^\n]*?\})-->([\s\S]*?)<!--bs:endif-->/g
const CART_RE =
  /<!--bs:cart_items-->([\s\S]*?)<!--bs:cart_fallback-->([\s\S]*?)<!--bs:\/cart_items-->/g
const TOKEN_RE = /\{\{\s*([a-z_]+)\s*\}\}/g

// Every token the backend substitutes at send time (campaign_content.
// build_token_context + LOYALTY_TOKEN_KEYS + the sender's brand_logo_url).
// A known token with no value sends as "" (e.g. reward_available and
// referral_link are always ""); an unknown token is left literally as
// {{x}}. item_* tokens are only filled inside cart rows (see renderPreview).
export const BACKEND_TOKENS: ReadonlySet<string> = new Set([
  'first_name',
  'last_name',
  'email',
  'coupon',
  'coupon_code',
  'store_name',
  'unsubscribe_url',
  'cart_url',
  'cart_id',
  'cart_total',
  'brand_logo_url',
  'points_balance',
  'tier_name',
  'next_tier',
  'points_to_next_tier',
  'tier_progress_pct',
  'member_id',
  'reward_available',
  'referral_link',
])

// Global (non-customer) sample values, matching the real send: {{coupon}} is
// the campaign's coupon code (the doc's Coupon block, '' without one) and
// {{cart_total}} is only filled for abandoned-cart emails.
export function globalSamples(
  ctx: Pick<CompileContext, 'storeName' | 'cartItems'> &
    Partial<Pick<CompileContext, 'brandLogoUrl'>>,
  couponCode: string,
): Record<string, string> {
  return {
    store_name: ctx.storeName || 'Your store',
    coupon: couponCode,
    coupon_code: couponCode,
    brand_logo_url: ctx.brandLogoUrl ?? '',
    cart_total: ctx.cartItems === false ? '' : '$94.98',
    cart_url: '#',
    unsubscribe_url: '#',
  }
}

// The value a token sends with for this sample customer: '' for a known
// token without one, undefined (leave {{key}} literal) for an unknown token.
export function sampleValue(
  key: string,
  c: SampleCustomer,
  globals: Record<string, string>,
): string | undefined {
  if (!BACKEND_TOKENS.has(key)) return undefined
  return c.vals[key] ?? globals[key] ?? ''
}

export function fillTokens(
  s: string,
  c: SampleCustomer,
  globals: Record<string, string>,
): string {
  return s.replace(TOKEN_RE, (m, k: string) => {
    const v = sampleValue(k, c, globals)
    return v == null ? m : escapeHtml(v)
  })
}

// The sample customer's cart: the first catalog products, else fixed samples.
export function sampleCartItems(
  c: SampleCustomer,
  ctx: Pick<CompileContext, 'catalog'>,
): CompileSample['cart'] {
  const catalogItems = (ctx.catalog?.all ?? []).slice(0, c.cartSize)
  return catalogItems.length
    ? catalogItems.map((p) => ({
        name: p.name,
        variant: [p.size, p.meta].filter(Boolean).join(' · '),
        price: p.salePrice ?? p.price,
        image: p.image,
      }))
    : SAMPLE_CART.slice(0, c.cartSize).map((p) => ({ ...p, image: '' }))
}

// What the canvas compile needs to render the wallet and cart rows for the
// "Preview as" customer (CompileContext.sample).
export function compileSample(
  c: SampleCustomer,
  ctx: Pick<CompileContext, 'catalog'>,
): CompileSample {
  return { member: c.member, vals: c.vals, cart: sampleCartItems(c, ctx) }
}

// Evaluates compiled email HTML (target 'email') for one sample customer the
// way the backend does at send time: bs:if markers, cart rows, tokens.
export function renderPreview(
  html: string,
  c: SampleCustomer,
  segments: Array<{ id: string }>,
  ctx: Pick<CompileContext, 'storeName' | 'catalog' | 'cartItems'> &
    Partial<Pick<CompileContext, 'brandLogoUrl'>>,
  couponCode: string,
): string {
  let out = html.replace(IF_RE, (_m, json: string, body: string) => {
    try {
      const cond = JSON.parse(json) as BlockCond
      return condMatches(cond, c, segments) ? body : ''
    } catch {
      return ''
    }
  })
  const items = sampleCartItems(c, ctx)
  out = out.replace(CART_RE, (_m, row: string, fallback: string) => {
    if (!items.length) return fallback
    return items
      .map((it) =>
        row
          .replace(/\{\{item_name\}\}/g, () => escapeHtml(it.name))
          .replace(/\{\{item_variant\}\}/g, () => escapeHtml(it.variant))
          .replace(/\{\{item_qty\}\}/g, '1')
          .replace(/\{\{item_price\}\}/g, () => money(it.price))
          .replace(/\{\{item_image\}\}/g, () =>
            escapeHtml(it.image || PLACEHOLDER_IMG),
          ),
      )
      .join('')
  })
  return fillTokens(out, c, globalSamples(ctx, couponCode))
}
